// Riverstone's To book › Asked to move (F-08; Blueprint's is AskedToMove.tsx): customers who asked,
// from their visit's link, for a different time (their picks, each a day and morning, afternoon or any
// time) or to cancel. Only asking (Q21): the visit stays as booked until someone here moves it
// (appointments.reschedule, from a pick), keeps it, or cancels it; then the request is closed
// (visitRequests.resolve). Live from the stream.

import { useEffect, useRef, useState } from 'react';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { DAY_WINDOWS, dayOptions, today as todayIn, windowLabel } from '../book-model.ts';
import { openJob } from '../job/job-page.ts';
import { Loading } from '../shared/Loading.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { phoneText } from '../types.ts';
import { askedText, requestChip, windowForPart, type VisitRequest } from './tobook-model.ts';
import { moveVisit, requestError, useVisitRequests } from './use-visit-requests.ts';

export function VisitRequestsSection({
  timeZone,
  canMove: mayMove,
  onChanged,
}: {
  timeZone: string;
  /** jobs.dispatch: move or cancel the visit here (others call, keep, or open the job). */
  canMove: boolean;
  /** A request was dealt with: To book's count re-reads. */
  onChanged: () => void;
}) {
  // While the company mirrors ServiceTitan its visits are moved and cancelled there; a request is still
  // kept or closed here, which changes only the request (BL-088)
  const mirroring = useMirror();
  const canMove = mayMove && !mirroring;
  const { list, error, toast, busy, keep, closeCancelled, cancelVisit, moved } = useVisitRequests(onChanged);
  const [moving, setMoving] = useState<VisitRequest | null>(null);

  return (
    <section className="sect" aria-label="Asked to move">
      <h2>Asked to move{list ? ` (${list.length})` : ''}</h2>
      {mirroring && mayMove && <p className="muted small">{MIRROR_LINE}</p>}
      {toast && (
        <p className="notice ok" role="status">
          {toast}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {list === null ? (
        !error && <Loading error={null} what="the requests" />
      ) : list.length ? (
        <div className="qgrid">
          {list.map((r) => {
            const chip = requestChip(r);
            const gone = r.visit.status === 'cancelled';
            // the job's only visit can't be cancelled on its own: the job is, on its sheet (QA-532)
            const withJob = !gone && r.kind === 'cancel' && r.visit.onlyVisit;
            return (
              <article key={r.id} className="stone qcard" aria-label={`${r.customer.name}: ${chip.text}`}>
                <div className="row between">
                  <b>{r.customer.name}</b>
                  <span className={`chip say ${chip.tone}`}>{chip.text}</span>
                </div>
                <span className="muted small">
                  Booked: {r.visit.whenText} · job #{r.visit.jobNumber} · {r.visit.summary}
                </span>
                {r.kind === 'change' && (
                  <ul className="picks" aria-label="Their picks">
                    {r.times.map((t) => (
                      <li key={`${t.date}${t.part}`}>{t.text}</li>
                    ))}
                  </ul>
                )}
                {r.note && <span className="small">“{r.note}”</span>}
                <span className="muted small">
                  {askedText(r.createdAt, timeZone)}{' '}
                  {gone
                    ? 'The visit is cancelled already: close the request.'
                    : withJob
                      ? 'It is the job’s only visit, so cancel the job: its sheet asks why.'
                      : 'Their visit stays booked until you act.'}
                </span>
                <div className="row wrap">
                  {gone && (
                    <button
                      className="btn primary"
                      disabled={busy === r.id}
                      onClick={() => void closeCancelled(r)}
                    >
                      Close the request
                    </button>
                  )}
                  {!gone && r.kind === 'change' && canMove && (
                    <button className="btn primary" onClick={() => setMoving(r)}>
                      Move it
                    </button>
                  )}
                  {withJob && canMove && (
                    <button className="btn primary" onClick={() => openJob(r.visit.jobId)}>
                      Cancel job #{r.visit.jobNumber}…
                    </button>
                  )}
                  {!gone && !withJob && r.kind === 'cancel' && canMove && (
                    <button
                      className="btn primary"
                      disabled={busy === r.id}
                      onClick={() => void cancelVisit(r)}
                    >
                      {busy === r.id ? 'Cancelling…' : 'Cancel the visit'}
                    </button>
                  )}
                  {r.customer.phone && (
                    <a className="btn soft" href={`tel:${r.customer.phone}`}>
                      Call {phoneText(r.customer.phone)}
                    </a>
                  )}
                  {!gone && (
                    <button className="btn quiet" disabled={busy === r.id} onClick={() => void keep(r)}>
                      Keep the visit
                    </button>
                  )}
                  <button className="btn quiet" onClick={() => openJob(r.visit.jobId)}>
                    Open job #{r.visit.jobNumber}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="muted empty">No one is asking to move or cancel a visit.</p>
      )}
      {moving && (
        <MoveDialog
          request={moving}
          timeZone={timeZone}
          onClose={() => setMoving(null)}
          onMoved={(when) => {
            setMoving(null);
            moved(moving, when);
          }}
        />
      )}
    </section>
  );
}

/** "Move it": the visit to one of their picks (or another day), then the request is closed. */
function MoveDialog({
  request,
  timeZone,
  onClose,
  onMoved,
}: {
  request: VisitRequest;
  timeZone: string;
  onClose: () => void;
  onMoved: (when: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const today = todayIn(timeZone);
  const first = request.times[0];
  const days = dayOptions(today, 60).slice(1);
  const picked = new Set(request.times.map((t) => t.date));
  const [date, setDate] = useState(first?.date ?? days[0]!.date);
  const [win, setWin] = useState(first ? windowForPart(first.part) : 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(`${request.id}:${date}:${win}`);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const move = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await moveVisit(request, date, win, timeZone, save.key);
      const day = days.find((d) => d.date === date)?.label ?? date;
      onMoved(`${day}, ${windowLabel(DAY_WINDOWS[win]!)}`);
    } catch (e) {
      setError(requestError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="move-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void move();
        }}
      >
        <h3 id="move-title">Move {request.customer.name}’s visit</h3>
        <p className="muted">
          Now: {request.visit.whenText}.{' '}
          {request.times.length ? `They asked for ${request.times.map((t) => t.text).join('; ')}.` : ''}
        </p>
        <label>
          Day
          <select className="field" value={date} onChange={(e) => setDate(e.target.value)}>
            {days.map((d) => (
              <option key={d.date} value={d.date}>
                {d.label}
                {picked.has(d.date) ? ' (they asked)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          Arrival window
          <select className="field" value={win} onChange={(e) => setWin(Number(e.target.value))}>
            {DAY_WINDOWS.map((w, i) => (
              <option key={i} value={i}>
                {windowLabel(w)}
              </option>
            ))}
          </select>
        </label>
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <SaveButton busy={busy} busyLabel="Moving…" error={error} onClick={() => void move()}>
            Move visit
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}
